import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cnMerge } from '../../utils/twmerge'
import type { UserSelectProps, UserOption } from './types'

export const UserSetorSelect: React.FC<UserSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione um usuário...",
  className,
  disabled = false,
  searchable = true,
  onSearch,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOption, setSelectedOption] = useState<UserOption | null>(
    options.find(opt => opt.value === value) || null
  )
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const filteredOptions = searchable && searchQuery
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Sincronizar selectedOption quando value mudar externamente
  useEffect(() => {
    const newSelectedOption = options.find(opt => opt.value === value) || null
    setSelectedOption(newSelectedOption)
  }, [value, options])

  // Calcular posição do dropdown quando aberto
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const dropdownHeight = Math.min(240, filteredOptions.length * 56 + 40 + 8) // 56px per user option + search input + padding
      const spaceBelow = viewport.height - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4 // 4px gap (mt-1)
      
      // Se não há espaço suficiente abaixo, mostrar acima
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setDropdownCoords({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [isOpen, filteredOptions.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    
    return () => {} // Return vazio quando não há listener
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  useEffect(() => {
    if (searchable && onSearch) {
      onSearch(searchQuery)
    }
  }, [searchQuery, searchable, onSearch])

  const handleOptionClick = (option: UserOption) => {
    if (option.disabled) return
    
    setSelectedOption(option)
    onChange?.(option.value)
    setIsOpen(false)
    setSearchQuery('')
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  return (
    <>
      <div className={cnMerge("relative", className)} ref={selectRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cnMerge(
            // Base styles
            'flex h-12 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm',
            // Normal border styles
            !error && 'border-gray-300 dark:border-neutral-600',
            // Error border styles
            error && 'border-red-500 dark:border-red-500',
            // Dark mode
            'dark:bg-neutral-800 dark:text-gray-100',
            // Focus styles
            'focus-visible:outline-none focus-visible:ring-2',
            !error && 'focus-visible:ring-blue-500/20 focus-visible:border-blue-500',
            error && 'focus-visible:ring-red-500/20 focus-visible:border-red-500',
            // Transition
            'transition-colors duration-200',
            // Disabled styles
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-neutral-700',
            // Hover
            !error && 'hover:border-gray-400 dark:hover:border-neutral-500',
            error && 'hover:border-red-600 dark:hover:border-red-400',
            // Open state
            isOpen && !error && 'border-blue-500 ring-2 ring-blue-500/20',
            isOpen && error && 'border-red-500 ring-2 ring-red-500/20'
          )}
        >
          <div className="flex items-center gap-3">
            {selectedOption ? (
              <>
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {selectedOption.avatar ? (
                    <img
                      src={selectedOption.avatar}
                      alt={selectedOption.name || selectedOption.label}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-600 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {getInitials(selectedOption.name || selectedOption.label)}
                    </div>
                  )}
                </div>
                {/* User Info */}
                <div className="flex flex-col text-left">
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {selectedOption.name || selectedOption.label}
                  </span>
                  {selectedOption.email && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedOption.email}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
            )}
          </div>
          <svg
            className={cnMerge(
              'h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="border-b border-gray-200 dark:border-neutral-700 p-2">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar usuário..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border-0 bg-transparent p-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
          )}

          {/* Options */}
          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleOptionClick(option)}
                  disabled={option.disabled}
                  className={cnMerge(
                    'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
                    'hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 focus:outline-none',
                    option.disabled && 'cursor-not-allowed opacity-50',
                    selectedOption?.value === option.value && 'bg-blue-50 dark:bg-blue-900/30'
                  )}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {option.avatar ? (
                      <img
                        src={option.avatar}
                        alt={option.name || option.label}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-600 text-xs font-medium text-gray-600 dark:text-gray-300">
                        {getInitials(option.name || option.label)}
                      </div>
                    )}
                  </div>
                  {/* User Info */}
                  <div className="flex flex-col">
                    <span className={cnMerge(
                      "font-medium",
                      selectedOption?.value === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'
                    )}>
                      {option.name || option.label}
                    </span>
                    {option.email && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {option.email}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                Nenhum usuário encontrado
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
